import { FormEvent, useState } from 'react';

import { getErrorMessage } from '@/hooks/useApi';

import { AdminGrantInput, GrantResponse, useAdminGrantTokens } from './useTokens';

export function TokensPage() {
  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastGrant, setLastGrant] = useState<{
    input: AdminGrantInput;
    result: GrantResponse;
  } | null>(null);

  const grantTokens = useAdminGrantTokens();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLastGrant(null);

    // Validate input
    if (!userId.trim()) {
      setError('User ID is required');
      return;
    }

    if (typeof amount !== 'number' || !Number.isInteger(amount) || amount <= 0) {
      setError('Amount must be a positive integer');
      return;
    }

    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    if (reason.length > 200) {
      setError('Reason must be 200 characters or less');
      return;
    }

    const input: AdminGrantInput = {
      userId: userId.trim(),
      amount,
      reason: reason.trim(),
    };

    try {
      const result = await grantTokens.mutateAsync(input);
      setLastGrant({ input, result });
      // Clear form on success
      setUserId('');
      setAmount('');
      setReason('');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Token Management</h1>
        <p className="mt-1 text-slate-600">Grant tokens to users</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Grant Form */}
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Grant Tokens</h2>

          {error && (
            <div role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="user-id" className="block text-sm font-medium text-slate-700">
                User ID <span className="text-red-500">*</span>
              </label>
              <input
                id="user-id"
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="e.g., abc123-def456-..."
                className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 font-mono text-sm focus:border-indigo-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-slate-500">
                The Cognito user ID (sub) from the user&apos;s account
              </p>
            </div>

            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-slate-700">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                id="amount"
                type="number"
                value={amount}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) {
                    setAmount('');
                    return;
                  }
                  const parsed = parseInt(raw, 10);
                  setAmount(Number.isNaN(parsed) ? '' : parsed);
                }}
                min={1}
                max={10000}
                placeholder="100"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-slate-500">Number of tokens to grant (1-10,000)</p>
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-slate-700">
                Reason <span className="text-red-500">*</span>
              </label>
              <input
                id="reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={200}
                placeholder="Beta tester reward"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-slate-500">
                This will be shown in the user&apos;s transaction history ({reason.length}/200)
              </p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={grantTokens.isPending}
                className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:bg-indigo-400"
              >
                {grantTokens.isPending ? 'Granting...' : 'Grant Tokens'}
              </button>
            </div>
          </form>
        </div>

        {/* Success Result */}
        {lastGrant && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6">
            <h2 className="text-lg font-semibold text-green-900 mb-4">Grant Successful</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-green-700">User ID</dt>
                <dd className="mt-1 font-mono text-sm text-green-900">{lastGrant.input.userId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-green-700">Amount Granted</dt>
                <dd className="mt-1 text-2xl font-bold text-green-900">
                  +{lastGrant.input.amount} tokens
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-green-700">New Balance</dt>
                <dd className="mt-1 text-lg font-semibold text-green-900">
                  {lastGrant.result.newBalance} tokens
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-green-700">Reason</dt>
                <dd className="mt-1 text-sm text-green-900">{lastGrant.input.reason}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-green-700">Transaction ID</dt>
                <dd className="mt-1 font-mono text-xs text-green-800">
                  {lastGrant.result.transactionId}
                </dd>
              </div>
            </dl>
          </div>
        )}

        {/* Info Panel (when no success result) */}
        {!lastGrant && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">About Token Grants</h2>
            <div className="space-y-3 text-sm text-slate-600">
              <p>
                Tokens are the virtual currency used to read new chapters in stories. Each new
                chapter costs 1 token.
              </p>
              <p>
                <strong>Free content:</strong> Root chapters (chapter 1) and nodes marked as free
                don&apos;t require tokens.
              </p>
              <p>
                <strong>Already visited:</strong> Revisiting a previously read chapter is always
                free.
              </p>
              <p>
                Use this form to grant tokens to users for beta testing, customer support, or
                promotional purposes.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
